import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Archive, ArrowLeft, RotateCcw, Trash2, MoreVertical, Loader2, AlertTriangle } from "lucide-react";
import Navbar from "@/components/Navbar";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageContainer } from "@/components/layout/PageContainer";
import { useArchivedProperties, useRestoreProperty, usePermanentDeleteProperty } from "@/hooks/useArchivedProperties";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

const ArchivedProperties = () => {
  const navigate = useNavigate();
  const { data: archivedProperties, isLoading } = useArchivedProperties();
  const restoreProperty = useRestoreProperty();
  const permanentDelete = usePermanentDeleteProperty();
  
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<{ id: string; name: string } | null>(null);
  const [confirmationText, setConfirmationText] = useState("");
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const handleRestore = async (propertyId: string, propertyName: string) => {
    setRestoringId(propertyId);
    try {
      await restoreProperty.mutateAsync(propertyId);
      toast({
        title: "Property restored",
        description: `${propertyName} has been restored to your active properties.`,
      });
      navigate("/owner/properties");
    } catch (error) {
      console.error("Error restoring property:", error);
      toast({
        title: "Error",
        description: "Failed to restore property. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRestoringId(null);
    }
  };

  const openDeleteModal = (propertyId: string, propertyName: string) => {
    setPropertyToDelete({ id: propertyId, name: propertyName });
    setDeleteModalOpen(true);
  };

  const handlePermanentDelete = async () => {
    if (!propertyToDelete) return;
    
    try {
      await permanentDelete.mutateAsync(propertyToDelete.id);
      toast({
        title: "Property permanently deleted",
        description: `${propertyToDelete.name} and all associated data have been permanently removed.`,
      });
      setDeleteModalOpen(false);
      setPropertyToDelete(null);
      setConfirmationText("");
    } catch (error) {
      console.error("Error permanently deleting property:", error);
      toast({
        title: "Error",
        description: "Failed to delete property. Please try again.",
        variant: "destructive",
      });
    }
  };

  const isConfirmationValid = confirmationText.trim().toLowerCase() === "delete forever";

  return (
    <AppLayout>
      <Navbar />
      
      {/* Header */}
      <div className="border-b bg-muted/30">
        <PageContainer className="py-4 sm:py-6">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/owner/properties">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back to Properties</span>
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Archive className="h-6 w-6 text-muted-foreground" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                Archived Properties
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {archivedProperties?.length || 0} archived {(archivedProperties?.length || 0) === 1 ? "property" : "properties"}
              </p>
            </div>
          </div>
        </PageContainer>
      </div>

      {/* Content */}
      <PageContainer className="py-4 sm:py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : archivedProperties?.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Archive className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No archived properties.</p>
              <p className="text-sm text-muted-foreground mt-1">
                Properties you archive will appear here.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate("/owner/properties")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Properties
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {archivedProperties?.map((property) => (
              <Card 
                key={property.id} 
                className="opacity-70 hover:opacity-90 transition-opacity border-muted"
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    {/* Property Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">
                        {property.name}
                      </h3>
                      {property.formatted_address && (
                        <p className="text-sm text-muted-foreground truncate mt-0.5">
                          {property.formatted_address}
                        </p>
                      )}
                      {property.archived_at && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Archived on {new Date(property.archived_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions - Desktop */}
                    <div className="hidden sm:flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                        onClick={() => handleRestore(property.id, property.name)}
                        disabled={restoringId === property.id}
                      >
                        {restoringId === property.id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <RotateCcw className="h-4 w-4 mr-2" />
                        )}
                        Restore
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive/30 hover:bg-destructive/10"
                        onClick={() => openDeleteModal(property.id, property.name)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Forever
                      </Button>
                    </div>

                    {/* Actions - Mobile (3-dot menu) */}
                    <div className="sm:hidden">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => handleRestore(property.id, property.name)}
                            disabled={restoringId === property.id}
                            className="text-green-600"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Restore Property
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => openDeleteModal(property.id, property.name)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Forever
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </PageContainer>

      {/* Permanent Delete Confirmation Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete property permanently?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to permanently delete <strong>{propertyToDelete?.name}</strong>?
              </p>
              <p className="text-destructive font-medium">
                This cannot be undone. All tasks, inventory, reports, checklists, 
                cleaner assignments, and billing history will be deleted forever.
              </p>
              <p className="font-medium text-foreground">
                To confirm, type <span className="font-bold">"delete forever"</span> below:
              </p>
              <Input
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="delete forever"
                className="mt-2"
                autoComplete="off"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={() => {
                setConfirmationText("");
                setPropertyToDelete(null);
              }}
              className="w-full sm:w-auto"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handlePermanentDelete}
              disabled={!isConfirmationValid || permanentDelete.isPending}
              className="w-full sm:w-auto"
            >
              {permanentDelete.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Forever
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
};

export default ArchivedProperties;
